/**
 * Utility functions for handling DOM attributes that React doesn't fully support
 */

import { RefObject, useEffect } from 'react';

/**
 * Sets non-standard attributes on a DOM element that React may not support properly
 * @param ref React ref to the DOM element
 * @param attributes Object containing attribute name-value pairs to set
 */
export function setNonStandardAttributes(
  element: HTMLElement | null,
  attributes: Record<string, string>
): void {
  if (!element) return;
  
  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(name, value);
    }
  });
}

/**
 * React hook to set non-standard HTML attributes on a DOM element
 * Use this for attributes that React doesn't support natively or that cause warnings
 * 
 * @example
 * const imgRef = useRef<HTMLImageElement>(null);
 * useNonStandardAttributes(imgRef, {
 *   fetchpriority: priority ? 'high' : 'auto',
 *   importance: priority ? 'high' : 'auto'
 * }, [priority]);
 */
export function useNonStandardAttributes<T extends HTMLElement>(
  ref: RefObject<T>,
  attributes: Record<string, string>,
  dependencies: React.DependencyList = []
): void {
  useEffect(() => {
    if (ref.current) {
      setNonStandardAttributes(ref.current, attributes);
    }
  }, [...dependencies]);
}

/**
 * List of HTML attributes that React doesn't support properly in camelCase
 * and should be set via setAttribute instead
 */
export const PROBLEMATIC_ATTRIBUTES = [
  'fetchpriority',
  'decoding',
  'loading',
  'referrerpolicy',
  'crossorigin',
  'playsinline',
  'autocapitalize',
]; 